import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import RoleSettingsRepository from '@/repositories/RoleSettingsRepository';
import { RoleSettingsResponse } from '@/types/roleSettings';
import { FrontendSettings } from '@/types/userSettings';

export const ROLE_SETTINGS_QUERY_KEY = 'roleSettings';

/**
 * The frontend defaults saved per staff role, and the ability to change them.
 *
 * Read once per page load and cached forever, like the user's own settings:
 * defaults only change because this tab changed them, and the mutation writes
 * the result straight back into the cache.
 */
const useRoleSettings = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const repository = RoleSettingsRepository();
  const queryClient = useQueryClient();

  const { data } = useQuery<RoleSettingsResponse>({
    enabled,
    queryKey: [ROLE_SETTINGS_QUERY_KEY],
    queryFn: () => repository.getRoleSettings(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // Defaults are a convenience: a failed read leaves the page on the
    // reader's own settings rather than toasting an error they can't act on.
    retry: false
  });

  const { mutateAsync, isPending: isSaving } = useMutation({
    mutationFn: ({ role, settings }: { role: string; settings: FrontendSettings }) =>
      repository.patchRoleSettings(role, settings),
    onSuccess: (frontendSettings, { role }) => {
      queryClient.setQueryData<RoleSettingsResponse>(
        [ROLE_SETTINGS_QUERY_KEY],
        (previous) =>
          previous && {
            ...previous,
            roles: previous.roles.map((entry) =>
              entry.slug === role ? { ...entry, frontend_settings: frontendSettings } : entry
            )
          }
      );
    }
  });

  const roles = data?.roles ?? [];

  /** The roles the reader works in, in the order the listing gives them. */
  const myRoles = roles.filter(({ slug }) => data?.current_roles.includes(slug));

  const settingsForRole = useCallback(
    (slug: string | null): FrontendSettings =>
      (slug && data?.roles.find((role) => role.slug === slug)?.frontend_settings) || {},
    [data]
  );

  return {
    canEdit: data?.can_edit ?? false,
    currentRole: data?.current_role ?? null,
    isLoaded: !!data,
    isSaving,
    myRoles,
    roles,
    saveRoleSettings: mutateAsync,
    settingsForRole
  };
};

export default useRoleSettings;
